import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { Alert, Badge, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { CostEstimateTable } from './CostEstimateTable';
import { formatDate, formatMoney, type HireDetail, statusName, statusTone } from './data';
import { CancelCard, DecisionCard, DecisionSummary, OriginTasksCard } from './RequestParts';

/** Detalle de un alta: los datos de la persona, el costo estimado, la decisión de RR. HH. y el avance de las tareas. */
export function HireDetailPage() {
  const { number = '' } = useParams();
  const location = useLocation();
  const hire = useQuery({ queryKey: ['usuarios', 'altas', 'detalle', number], queryFn: () => api<HireDetail>(`/api/usuarios/altas/${number}`) });
  const [notice, setNotice] = useState<string | null>(null);

  if (!hire.data) return hire.error ? <Alert>{errorMessage(hire.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  const r = hire.data;
  const created = (location.state as { created?: boolean } | null)?.created;
  const hasCost = r.estimate.monthly > 0 || r.estimate.upfront > 0;

  return (
    <div className="space-y-4">
      <div>
        <Link to="/usuarios/altas" className="text-sm text-marca hover:underline">
          ← {t('us.altas_titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">
          <span className="mr-2 font-mono text-base text-slate-500">{r.code}</span>
          {r.fullName}
        </h1>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
          <Badge tone={statusTone(r.status)}>{statusName(r.status)}</Badge>
          <span className="text-slate-500">· {r.organization}</span>
        </div>
      </div>
      {created && <Alert tone="success">{t('us.alta_creada', { codigo: r.code })}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}
      <DecisionSummary status={r.status} decidedBy={r.decidedBy} decidedAt={r.decidedAt} reason={r.decisionReason} />

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <div className="space-y-4">
          <Card title={t('us.datos_persona')}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
              <dt className="text-slate-500">{t('us.campo_correo')}</dt>
              <dd>{r.email}</dd>
              <dt className="text-slate-500">{t('us.campo_departamento')}</dt>
              <dd>{r.department ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_cargo')}</dt>
              <dd>{r.position ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_jefe')}</dt>
              <dd>{r.manager ?? '—'}</dd>
              <dt className="text-slate-500">{t('us.campo_ingreso')}</dt>
              <dd>{formatDate(r.startDate)}</dd>
              <dt className="text-slate-500">{t('us.col_solicitante')}</dt>
              <dd>
                {r.requester ?? '—'} · {formatDate(r.createdAt)}
              </dd>
            </dl>
            {r.notes && <p className="mt-3 whitespace-pre-wrap border-t border-slate-100 pt-3 text-sm text-slate-700">{r.notes}</p>}
          </Card>
          <Card title={t('us.campo_licencias')}>
            {r.licenses.length === 0 ? <p className="text-sm text-slate-500">{t('us.sin_licencias_pedidas')}</p> : <CostEstimateTable estimate={r.estimate} />}
          </Card>
          <OriginTasksCard tasks={r.tasks} empty={r.status === 'aprobada' ? t('us.sin_tareas') : t('us.tareas_al_aprobar_alta')} />
        </div>
        <div className="space-y-4">
          {r.can.decide && (
            <DecisionCard
              path={`/api/usuarios/altas/${r.number}`}
              help={t('us.decidir_alta_ayuda')}
              warning={
                hasCost ? (
                  <Alert tone="warning">
                    <p className="font-medium">{t('us.advertencia_costo', { mensual: formatMoney(r.estimate.monthly), inicial: formatMoney(r.estimate.upfront) })}</p>
                    <p className="mt-1">{t('us.advertencia_politica')}</p>
                  </Alert>
                ) : undefined
              }
              confirm={hasCost ? t('us.confirmo_costo') : undefined}
              onDone={(approved) => setNotice(approved ? t('us.alta_aprobada_ok', { nombre: r.fullName }) : t('us.rechazada_ok'))}
            />
          )}
          {r.can.cancel && <CancelCard path={`/api/usuarios/altas/${r.number}`} />}
        </div>
      </div>
    </div>
  );
}
